import { Container, Text, TextStyle } from 'pixi.js';
import { UIScaleManager } from '../utils/UIScaleManager';
import { MobileDeviceManager } from '../utils/MobileDetection';

/**
 * ControlHints configuration
 */
export interface ControlHintsConfig {
  /** Padding from bottom-left of screen */
  bottomPadding: number;
  /** Padding from left of screen */
  leftPadding: number;
}

const DEFAULT_CONFIG: ControlHintsConfig = {
  bottomPadding: 20,
  leftPadding: 20,
};

/**
 * ControlHints - PixiJS UI component for displaying game controls
 *
 * Shows keyboard shortcuts for boost and slow mode
 * Positioned in the bottom-left corner
 */
export class ControlHints {
  /** Main container */
  public container: Container;

  /** Hint text */
  private hintText: Text;

  /** Configuration */
  private config: ControlHintsConfig;

  /** Screen height for positioning */
  private screenHeight: number = 0;

  /** UI Scale Manager reference */
  private scaleManager: UIScaleManager;

  /** Mobile device manager reference */
  private mobileManager: MobileDeviceManager;

  constructor(config: Partial<ControlHintsConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.scaleManager = UIScaleManager.getInstance();
    this.mobileManager = MobileDeviceManager.getInstance();
    this.container = new Container();
    this.container.label = 'ControlHints';

    // Create hint text with keyboard shortcuts
    this.hintText = new Text({
      text: '[SPACE] Boost   |   [SHIFT] Slow Crawl   |   [RELEASE] +Score',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: this.scaleManager.scaleFontSize(12),
        fontWeight: 'normal',
        fill: 0xaaaaaa,
        letterSpacing: 0.5,
        dropShadow: {
          alpha: 0.6,
          blur: 2,
          distance: 1,
          color: 0x000000,
        },
      }),
    });
    this.hintText.anchor.set(0, 1); // Bottom-left anchor
    this.container.addChild(this.hintText);

    // Set initial alpha (slightly transparent)
    this.container.alpha = 0.7;

    // Hide on mobile devices (touch controls have their own hints)
    if (this.mobileManager.isMobile) {
      this.container.visible = false;
    }

    // Listen for scale changes
    this.scaleManager.addListener(() => this.applyScaling());
  }

  /**
   * Apply current scaling to all elements
   */
  private applyScaling(): void {
    (this.hintText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(12);
  }

  /**
   * Update position when screen is resized
   */
  onResize(_width: number, height: number): void {
    this.screenHeight = height;
    this.updatePosition();
  }

  /**
   * Update the position based on screen dimensions
   */
  private updatePosition(): void {
    this.container.x = this.config.leftPadding;
    this.container.y = this.screenHeight - this.config.bottomPadding;
  }

  /**
   * Show the control hints
   */
  show(): void {
    this.container.visible = true;
  }

  /**
   * Hide the control hints
   */
  hide(): void {
    this.container.visible = false;
  }

  /**
   * Set visibility
   */
  setVisible(visible: boolean): void {
    this.container.visible = visible;
  }
}
